import { defineStore } from 'pinia';

import type { Profile, ProfileContent } from '@/features/Profile/types';
import { buildProfileSelectData } from '@/features/Profile/utils';
import { useApi } from '@/services/api/useApi';
import { VGL_ERROR_CODE } from '@/services/errorHandler/errorCodes';
import { getErrorMessage } from '@/services/errorHandler/utils';
import { VglError } from '@/services/errorHandler/vglError';
import type { ResourceContainer } from '@/types';
import { transformDateStrings } from '@/utils/dates/transformDates';
import dot from 'dot-object';

interface ProfileStoreState extends ResourceContainer<Profile[]> {
  data: Profile[];
  selected: Profile | null;
}

export const useProfileStore = defineStore('ProfileStore', {
  state: (): ProfileStoreState => ({
    data: [],
    selected: null,
    isLoading: false,
  }),

  actions: {
    async setActive(profile: Profile) {
      this.selected = profile;

      await this.loadSelectedProfile();
    },

    async loadProfiles() {
      try {
        this.isLoading = true;
        const profileApi = useApi().profile;
        const [, resultPromise] = profileApi.loadProfiles();

        const result = await resultPromise;

        this.data = result?.profiles ?? [];
      } catch (error) {
        throw new VglError(VGL_ERROR_CODE.PROFILE_LOADING_FAILED, {
          additionalErrorMessage: `Error: ${getErrorMessage(error)}`,
          cause: error,
        });
      } finally {
        this.isLoading = false;
      }
    },

    async storeProfile(
      title: string,
      content: ProfileContent,
      active = true,
      setSelected = false,
    ) {
      try {
        this.isLoading = true;
        const profileApi = useApi().profile;
        const [, resultPromise] = profileApi.storeProfile({
          title,
          active,
          content,
        });

        const result = await resultPromise;
        await this.loadProfiles();

        if (setSelected) {
          if (result === null) {
            throw Error('missing profile information');
          }

          this.setActive(result.profile);
        }
      } catch (error) {
        throw new VglError(VGL_ERROR_CODE.PROFILE_STORING_FAILED, {
          additionalErrorMessage: `Error: ${getErrorMessage(
            error,
          )} - Title: ${title} - Content: ${JSON.stringify(
            content,
          )} - Active: ${active ? 'yes' : 'no'} - SetSelected: ${
            setSelected ? 'yes' : 'no'
          }`,
          cause: error,
        });
      } finally {
        this.isLoading = false;
      }
    },

    async saveProfile(profile: Profile) {
      try {
        this.isLoading = true;
        const profileApi = useApi().profile;
        const [, resultPromise] = profileApi.updateProfile(profile);
        await resultPromise;
        await this.loadProfiles();
      } catch (error) {
        throw new VglError(VGL_ERROR_CODE.PROFILE_UPDATING_FAILED, {
          additionalErrorMessage: `Error: ${getErrorMessage(
            error,
          )} - Profile: ${JSON.stringify(profile)} `,
          cause: error,
        });
      } finally {
        this.isLoading = false;
      }
    },

    async deleteProfile(profileId: number) {
      try {
        this.isLoading = true;
        const profileApi = useApi().profile;
        const [, resultPromise] = profileApi.deleteProfile(profileId);
        await resultPromise;
        await this.loadProfiles();
      } catch (error) {
        throw new VglError(VGL_ERROR_CODE.PROFILE_DELETING_FAILED, {
          additionalErrorMessage: `Error: ${getErrorMessage(
            error,
          )} - Profile: ${profileId} `,
          cause: error,
        });
      } finally {
        this.isLoading = false;
      }
    },

    async loadSelectedProfile(profileId?: number) {
      let id = profileId;
      if (id === undefined && this.selected !== null) {
        id = this.selected.id;
      }

      if (
        (this.selected === null && profileId === undefined) ||
        this.selected?.content !== undefined ||
        id === undefined
      ) {
        return;
      }

      try {
        this.isLoading = true;
        const profileApi = useApi().profile;
        const [, resultPromise] = profileApi.loadProfile(id);
        const res = await resultPromise;

        if (res === null) {
          throw new Error('missing profile information');
        }

        res.profile.content.eingabedaten = dot.object(
          res.profile.content.eingabedaten,
        ) as Record<string, unknown>;

        this.selected = transformDateStrings(res.profile);
      } catch (error) {
        throw new VglError(VGL_ERROR_CODE.PROFILE_LOADING_SELECTED_FAILED, {
          additionalErrorMessage: `Error: ${getErrorMessage(
            error,
          )} - Profile: ${id} `,
          cause: error,
        });
      } finally {
        this.isLoading = false;
      }
    },
  },

  getters: {
    forSelect: (state) => buildProfileSelectData(state.data),
  },
});
